import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import QRCodeStyling from 'qr-code-styling';
import { FaCopy, FaCheck, FaDownload } from 'react-icons/fa';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import frame1 from '../assets/frames/frame-1.svg';
import frame2 from '../assets/frames/frame-2.svg';
import frame3 from '../assets/frames/frame-3.svg';
import frame4 from '../assets/frames/frame-4.svg';
import frame5 from '../assets/frames/frame-5.svg';
import frame6 from '../assets/frames/frame-6.svg';
import SubscriptionPlan from '../components/SubscriptionPlan';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
// Normalize frontend URL (remove trailing slash to prevent double slashes)
const FRONTEND_URL = (process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000').replace(/\/+$/, '');

// Helper function to get initials from full name
const getInitials = (fullName) => {
  if (!fullName) return '?';
  const nameParts = fullName.trim().split(/\s+/);
  if (nameParts.length >= 2) {
    // First letter of first name + first letter of last name
    return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
  } else if (nameParts.length === 1) {
    // If only one name, use first two letters
    return nameParts[0].substring(0, 2).toUpperCase();
  }
  return fullName.charAt(0).toUpperCase();
};

// Helper function to get a color based on name (for consistent avatar colors)
const getAvatarColor = (name) => {
  if (!name) return '#667eea';
  const colors = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Purple
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // Pink
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Blue
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Green
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // Orange
    'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', // Teal
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', // Light
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', // Rose
  ];
  // Use name to consistently pick a color
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [vcards, setVcards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [failedImages, setFailedImages] = useState(new Set());
  const renderedQRs = useRef(new Set());

  useEffect(() => {
    fetchVCards();
  }, []);

  const fetchVCards = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/vcards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVcards(response.data);
    } catch (error) {
      console.error('Error fetching vCards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vCard?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/vcards/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchVCards();
    } catch (error) {
      alert('Failed to delete vCard');
    }
  };

  const handleCopyUrl = async (uniqueId) => {
    const url = `${FRONTEND_URL}/vcard/${uniqueId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(uniqueId);
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(uniqueId);
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    }
  };

  // Common method to get QR positioning for frames
  const getQRFramePosition = (frameId) => {
    // Frame SVG viewBox is 64x84
    const framePositions = {
      'frame-1': {
        // Frame-1: qr-background is a rounded rect at x=6, y=10, width=52, height=44
        top: '11.9%',    // 10/84 = 11.9%
        left: '9.375%',  // 6/64 = 9.375%
        width: '81.25%', // 52/64 = 81.25%
        height: '52.38%', // 44/84 = 52.38%
        padding: '4px'
      },
      'frame-2': {
        // Frame-2: qr-background path M6 10 with rounded corners (radius 4)
        top: '6.5%',     // Slightly above 6/84 = 7.14% to cover border
        left: '8.9%',    // Slightly left of 6/64 = 9.375% to cover border
        width: '82.5%',  // Slightly larger than 52/64 = 81.25% to cover border
        height: '63.1%', // Slightly larger than 52/84 = 61.9% to cover border
        padding: '1px'   // Minimal padding
      },
      'frame-3': {
        // Frame-3: Similar to frame-1, qr-background at x=6, y=10, width=52, height=44
        top: '11.9%',
        left: '9.375%',
        width: '81.25%',
        height: '52.38%',
        padding: '4px'
      },
      'frame-4': {
        // Frame-4: qr-background rect at x=11, y=24, width=42, height=42
        top: '28.57%',   // 24/84 = 28.57%
        left: '17.19%',  // 11/64 = 17.19%
        width: '65.63%', // 42/64 = 65.63%
        height: '50%',   // 42/84 = 50%
        padding: '2px'
      },
      'frame-5': {
        // Frame-5: qr-background at x=6, y=10, width=52, height=44
        top: '11.9%',
        left: '9.375%',
        width: '81.25%',
        height: '52.38%',
        padding: '4px'
      },
      'frame-6': {
        // Frame-6: qr-background rect at x=14.5, y=24, width=35, height=35
        top: '28.57%',   // 24/84 = 28.57%
        left: '22.66%',  // 14.5/64 = 22.66%
        width: '54.69%', // 35/64 = 54.69%
        height: '41.67%', // 35/84 = 41.67%
        padding: '2px'
      }
    };
    return framePositions[frameId] || framePositions['frame-1'];
  };

  // Function to modify frame SVG with custom text and colors
  const getModifiedFrameSVG = async (framePath, frameText, frameTextColor, frameColor, frameBackgroundColor) => {
    try {
      const response = await fetch(framePath);
      let svgText = await response.text();
      
      // Replace text content (handles multiline)
      svgText = svgText.replace(/(<text[\s\S]*?>)(.*?)(<\/text>)/g, (match, openTag, oldText, closeTag) => {
        return `${openTag}${frameText || 'Scan me!'}${closeTag}`;
      });
      
      // Update text color (handles multiline)
      svgText = svgText.replace(/(<text[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldColor, afterFill) => {
        return `${beforeFill}${fillAttr}${frameTextColor || '#000000'}${afterFill}`;
      });
      
      // If text doesn't have fill attribute, add it (handles multiline)
      svgText = svgText.replace(/(<text[\s\S]*?)(>)/g, (match, tag, closing) => {
        if (!tag.includes('fill=')) {
          return `${tag} fill="${frameTextColor || '#000000'}"${closing}`;
        }
        return match;
      });
      
      // Update elements with id="frame" (paths or groups) - apply frame color (handles multiline)
      // Handle path and group elements with id="frame"
      svgText = svgText.replace(/(<(?:path|g)[\s\S]*?id="frame"[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldColor, afterFill) => {
        // Skip if it's transparent or none (those are borders, handled by stroke)
        if (oldColor !== 'transparent' && oldColor !== 'none') {
          return `${beforeFill}${fillAttr}${frameColor || '#000000'}${afterFill}`;
        }
        return match;
      });
      
      // Update elements inside text-container - these should get frame color (handles multiline)
      // Find text-container and update path fills within it
      const textContainerRegex = /(<g[\s\S]*?id="text-container"[\s\S]*?>)([\s\S]*?)(<\/g>)/g;
      svgText = svgText.replace(textContainerRegex, (match, openTag, content, closeTag) => {
        // Update path fills within text-container (button backgrounds) - handles multiline
        const updatedContent = content.replace(/(<path[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (pathMatch, beforeFill, fillAttr, pathColor, afterFill) => {
          // Skip if it's white (text might be on white background)
          if (pathColor.toLowerCase() !== 'white' && pathColor !== '#ffffff' && pathColor !== '#FFFFFF') {
            return `${beforeFill}${fillAttr}${frameColor || '#000000'}${afterFill}`;
          }
          return pathMatch;
        });
        // Update path strokes within text-container
        const updatedContent2 = updatedContent.replace(/(<path[\s\S]*?)(stroke=")([^"]*)("[\s\S]*?>)/g, (pathMatch, beforeStroke, strokeAttr, pathColor, afterStroke) => {
          if (pathColor !== 'transparent' && pathColor !== 'none') {
            return `${beforeStroke}${strokeAttr}${frameColor || '#000000'}${afterStroke}`;
          }
          return pathMatch;
        });
        return openTag + updatedContent2 + closeTag;
      });
      
      // Update all path fills that are borders/lines (not qr-background, frame-background, or icons) - handles multiline
      svgText = svgText.replace(/(<path[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldColor, afterFill) => {
        // Skip qr-background and frame-background (handled separately)
        if (match.includes('qr-background') || match.includes('frame-background')) {
          return match;
        }
        // Skip icon paths (youtube icon should stay white)
        if (match.includes('id="youtube"')) {
          return match;
        }
        // Skip transparent fills
        if (oldColor === 'transparent' || oldColor === 'none') {
          return match;
        }
        
        // Check context to determine if this is a border/line element
        const fullMatch = beforeFill + fillAttr + oldColor + afterFill;
        const matchIndex = svgText.indexOf(fullMatch);
        if (matchIndex !== -1) {
          const beforeMatch = svgText.substring(Math.max(0, matchIndex - 200), matchIndex);
          const pathElement = svgText.substring(matchIndex, Math.min(svgText.length, matchIndex + 200));
          
          // If it's a horizontal line (like frame-1's M1 62h62v2H1z) or other border line
          // Or if it's an arrow path (frame-5) or other decorative element
          // Update to frame color
          if (pathElement.includes('h62') || pathElement.includes('v2') || 
              pathElement.includes('id="arrow"') ||
              (!beforeMatch.includes('text-container') && !beforeMatch.includes('qr-background') && !beforeMatch.includes('frame-background'))) {
            return `${beforeFill}${fillAttr}${frameColor || '#000000'}${afterFill}`;
          }
        }
        // Default: don't change
        return match;
      });
      
      // Update all strokes (borders) with frame color - need to match in context (handles multiline)
      // Match stroke within path/rect/g elements, but skip qr-background and frame-background
      svgText = svgText.replace(/(<(?:path|rect|g)[\s\S]*?)(stroke=")([^"]*)("[\s\S]*?>)/g, (match, beforeStroke, strokeAttr, oldColor, afterStroke) => {
        // Don't replace if it's part of qr-background or frame-background (frame-background stroke handled separately)
        if (match.includes('qr-background') || match.includes('frame-background')) {
          return match;
        }
        // Don't replace icon paths
        if (match.includes('id="youtube"')) {
          return match;
        }
        // Don't replace transparent or none strokes
        if (oldColor === 'transparent' || oldColor === 'none') {
          return match;
        }
        // Update all other strokes to frame color
        return `${beforeStroke}${strokeAttr}${frameColor || '#000000'}${afterStroke}`;
      });
      
      // Update rect fills for frame elements (not frame-background or qr-background) - handles multiline
      svgText = svgText.replace(/(<rect[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldColor, afterFill) => {
        // Skip if already handled (frame-background or qr-background)
        if (match.includes('id="frame-background"') || match.includes('id="qr-background"')) {
          return match; // Already handled above
        }
        // Skip transparent fills
        if (oldColor === 'transparent' || oldColor === 'none') {
          return match;
        }
        // Update other rect fills to frame color
        return `${beforeFill}${fillAttr}${frameColor || '#000000'}${afterFill}`;
      });
      
      // Update qr-background fill color - make it transparent so QR code shows through (handles multiline)
      svgText = svgText.replace(/(<(?:path|rect)[\s\S]*?id="qr-background"[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldColor, afterFill) => {
        // Make qr-background transparent so QR code shows through without grey background
        return `${beforeFill}${fillAttr}transparent${afterFill}`;
      });
      
      // If qr-background doesn't have fill attribute, add it as transparent (handles multiline)
      svgText = svgText.replace(/(<(?:path|rect)[\s\S]*?id="qr-background"[\s\S]*?)(>)/g, (match, tag, closing) => {
        if (!tag.includes('fill=')) {
          return `${tag} fill="transparent"${closing}`;
        }
        return match;
      });
      
      // Remove stroke from qr-background to eliminate any grey border (handles multiline)
      svgText = svgText.replace(/(<(?:path|rect)[\s\S]*?id="qr-background"[\s\S]*?)(stroke=")([^"]*)("[\s\S]*?>)/g, (match, beforeStroke, strokeAttr, oldColor, afterStroke) => {
        return `${beforeStroke}${strokeAttr}none${afterStroke}`;
      });
      
      // If qr-background doesn't have stroke attribute, ensure it won't show a border (handles multiline)
      svgText = svgText.replace(/(<(?:path|rect)[\s\S]*?id="qr-background"[\s\S]*?)(>)/g, (match, tag, closing) => {
        if (!tag.includes('stroke=')) {
          return `${tag} stroke="none"${closing}`;
        }
        return match;
      });
      
      // Update frame-background element (can be rect or path) - this is the main background
      // Find the element and update its fill attribute directly
      
      // Method 1: If fill attribute exists, replace it (handles multiline)
      svgText = svgText.replace(/(<(?:rect|path)[\s\S]*?id="frame-background"[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldValue, afterFill) => {
        return `${beforeFill}${fillAttr}${frameBackgroundColor || '#FFFFFF'}${afterFill}`;
      });
      
      // Method 2: If fill attribute doesn't exist, add it before the closing >
      svgText = svgText.replace(/(<(?:rect|path)[\s\S]*?id="frame-background"[\s\S]*?)(>)/g, (match, tag, closing) => {
        if (!tag.includes('fill=')) {
          // Add fill before the closing >
          return `${tag} fill="${frameBackgroundColor || '#FFFFFF'}"${closing}`;
        }
        return match;
      });
      
      // Update stroke color on frame-background to frame color (for borders)
      svgText = svgText.replace(/(<(?:rect|path)[\s\S]*?id="frame-background"[\s\S]*?)(stroke=")([^"]*)("[\s\S]*?>)/g, (match, beforeStroke, strokeAttr, oldValue, afterStroke) => {
        if (oldValue !== 'transparent' && oldValue !== 'none') {
          return `${beforeStroke}${strokeAttr}${frameColor || '#000000'}${afterStroke}`;
        }
        return match;
      });
      
      // Update elements with class="frame-color" - apply frame background color (handles multiline)
      // Method 1: If fill attribute exists, replace it
      svgText = svgText.replace(/(<(?:rect|path|circle|ellipse|polygon|polyline|line)[\s\S]*?class="frame-color"[\s\S]*?)(fill=")([^"]*)("[\s\S]*?>)/g, (match, beforeFill, fillAttr, oldValue, afterFill) => {
        return `${beforeFill}${fillAttr}${frameBackgroundColor || '#FFFFFF'}${afterFill}`;
      });
      
      // Method 2: If fill attribute doesn't exist, add it before the closing >
      svgText = svgText.replace(/(<(?:rect|path|circle|ellipse|polygon|polyline|line)[\s\S]*?class="frame-color"[\s\S]*?)(>)/g, (match, tag, closing) => {
        if (!tag.includes('fill=')) {
          // Add fill before the closing >
          return `${tag} fill="${frameBackgroundColor || '#FFFFFF'}"${closing}`;
        }
        return match;
      });
      
      const blob = new Blob([svgText], { type: 'image/svg+xml' });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error modifying frame SVG:', error);
      return framePath;
    }
  };

  // Render styled QR code with frame
  const renderStyledQR = useCallback(async (vcard, containerId) => {
    const container = document.getElementById(containerId);
    if (!container) return;

    // Check if already rendered to prevent duplicates
    if (renderedQRs.current.has(containerId)) {
      return; // Already rendered
    }
    
    // Mark as rendering
    renderedQRs.current.add(containerId);

    const qrCodeUrl = `${FRONTEND_URL}/vcard/${vcard.uniqueId}`;
    const qrConfig = vcard.qrConfig || {};
    
    // Default QR config
    const defaultConfig = {
      type: 'canvas',
      shape: 'square',
      width: 200,
      height: 200,
      data: qrCodeUrl,
      margin: 0,
      qrOptions: {
        typeNumber: '0',
        mode: 'Byte',
        errorCorrectionLevel: 'Q'
      },
      dotsOptions: {
        type: qrConfig.dotsOptions?.type || 'square',
        color: qrConfig.dotsOptions?.color || '#000000'
      },
      backgroundOptions: {
        color: qrConfig.backgroundOptions?.color || '#ffffff'
      },
      cornersSquareOptions: {
        type: qrConfig.cornersSquareOptions?.type || 'square',
        color: qrConfig.cornersSquareOptions?.color || '#000000'
      },
      cornersDotOptions: {
        type: qrConfig.cornersDotOptions?.type || 'square',
        color: qrConfig.cornersDotOptions?.color || '#000000'
      },
      image: qrConfig.image || null
    };

    // Clear container completely
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    if (qrConfig.frameId) {
      // Render with frame
      const frameMap = {
        'frame-1': frame1,
        'frame-2': frame2,
        'frame-3': frame3,
        'frame-4': frame4,
        'frame-5': frame5,
        'frame-6': frame6
      };
      
      const originalFramePath = frameMap[qrConfig.frameId];
      if (originalFramePath) {
        const frameText = qrConfig.frameText || 'Scan me!';
        const frameTextColor = qrConfig.frameTextColor || '#000000';
        const frameColor = qrConfig.frameColor || '#000000';
        const frameBackgroundColor = qrConfig.frameBackgroundColor || '#FFFFFF';
        
        // Always regenerate frame from saved customization values
        // (blob URLs don't persist after page reload)
        const modifiedFrameUrl = await getModifiedFrameSVG(
          originalFramePath,
          frameText,
          frameTextColor,
          frameColor,
          frameBackgroundColor
        );

        // Create frame container
        const frameWrapper = document.createElement('div');
        frameWrapper.className = 'dashboard-qr-frame-wrapper';
        frameWrapper.style.position = 'relative';
        frameWrapper.style.display = 'inline-block';
        frameWrapper.style.width = '100%';
        frameWrapper.style.maxWidth = '200px';
        frameWrapper.style.margin = '0 auto';

        const frameImg = document.createElement('img');
        frameImg.src = modifiedFrameUrl;
        frameImg.alt = 'Frame';
        frameImg.style.width = '100%';
        frameImg.style.height = 'auto';
        frameImg.style.display = 'block';
        frameImg.style.position = 'relative';
        frameImg.style.zIndex = '1';
        frameImg.style.pointerEvents = 'none';

        const qrContainer = document.createElement('div');
        qrContainer.className = 'dashboard-qr-in-frame';
        qrContainer.style.position = 'absolute';
        
        // Use common method to get frame positioning
        const framePos = getQRFramePosition(qrConfig.frameId);
        qrContainer.style.top = framePos.top;
        qrContainer.style.left = framePos.left;
        qrContainer.style.width = framePos.width;
        qrContainer.style.height = framePos.height;
        qrContainer.style.zIndex = '2';
        qrContainer.style.display = 'flex';
        qrContainer.style.alignItems = 'center';
        qrContainer.style.justifyContent = 'center';
        qrContainer.style.overflow = 'hidden';
        qrContainer.style.padding = framePos.padding;
        qrContainer.style.boxSizing = 'border-box';

        // Adjust QR size for frame - scale to fit grey area properly
        // Use consistent sizing for all frames
        defaultConfig.width = 200;
        defaultConfig.height = 200;

        // Clear qrContainer before appending
        qrContainer.innerHTML = '';
        
        const qrCode = new QRCodeStyling(defaultConfig);
        qrCode.append(qrContainer);

        frameWrapper.appendChild(frameImg);
        frameWrapper.appendChild(qrContainer);
        
        // Clear container and append frame wrapper
        container.innerHTML = '';
        container.appendChild(frameWrapper);
      }
    } else {
      // Render without frame
      defaultConfig.width = 200;
      defaultConfig.height = 200;
      const qrCode = new QRCodeStyling(defaultConfig);
      qrCode.append(container);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Download QR code without frame
  const handleDownloadQR = async (vcard) => {
    try {
      const qrCodeUrl = `${FRONTEND_URL}/vcard/${vcard.uniqueId}`;
      const qrConfig = vcard.qrConfig || {};
      
      const defaultConfig = {
        type: 'canvas',
        shape: 'square',
        width: 500,
        height: 500,
        data: qrCodeUrl,
        margin: 0,
        qrOptions: {
          typeNumber: '0',
          mode: 'Byte',
          errorCorrectionLevel: 'Q'
        },
        dotsOptions: {
          type: qrConfig.dotsOptions?.type || 'square',
          color: qrConfig.dotsOptions?.color || '#000000'
        },
        backgroundOptions: {
          color: qrConfig.backgroundOptions?.color || '#ffffff'
        },
        cornersSquareOptions: {
          type: qrConfig.cornersSquareOptions?.type || 'square',
          color: qrConfig.cornersSquareOptions?.color || '#000000'
        },
        cornersDotOptions: {
          type: qrConfig.cornersDotOptions?.type || 'square',
          color: qrConfig.cornersDotOptions?.color || '#000000'
        },
        image: qrConfig.image || null
      };

      const qrCode = new QRCodeStyling(defaultConfig);
      const blob = await qrCode.getRawData('png');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${vcard.fullName.replace(/\s+/g, '_')}_QR.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading QR code:', error);
      alert('Failed to download QR code');
    }
  };

  // Initialize QR codes when vcards are loaded
  useEffect(() => {
    // Clear previous rendered tracking
    renderedQRs.current.clear();
    
    if (vcards.length > 0) {
      const timeouts = [];
      vcards.forEach((vcard) => {
        const containerId = `qr-container-${vcard._id}`;
        const timeoutId = setTimeout(() => {
          renderStyledQR(vcard, containerId);
        }, 100);
        timeouts.push(timeoutId);
      });
      
      // Cleanup function
      return () => {
        timeouts.forEach(timeoutId => clearTimeout(timeoutId));
        // Clear all QR containers and reset rendered tracking
        const currentRenderedQRs = new Set(renderedQRs.current);
        vcards.forEach((vcard) => {
          const containerId = `qr-container-${vcard._id}`;
          const container = document.getElementById(containerId);
          if (container) {
            container.innerHTML = '';
          }
          currentRenderedQRs.delete(containerId);
        });
      };
    }
  }, [vcards, renderStyledQR]);

  if (loading) {
    return <div className="dashboard-loading">Loading your vCards...</div>;
  }

  return (
    <div className="dashboard">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <h1>My vCards</h1>
            <p>Welcome back, {user?.name}!</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end' }}>
            <Link to="/vcard/create" className="btn btn-primary">
              + Create New vCard
            </Link>
            <SubscriptionPlan />
          </div>
        </div>

        {vcards.length === 0 ? (
          <div className="empty-dashboard">
            <h2>You haven't created any vCards yet</h2>
            <p>Create your first digital business card to get started!</p>
            <Link to="/vcard/create" className="btn btn-primary btn-large">
              Create Your First vCard
            </Link>
          </div>
        ) : (
          <div className="vcards-grid">
            {vcards.map((vcard) => (
              <div key={vcard._id} className="vcard-card">
                <div className="vcard-card-header">
                  {vcard.profileImage && !failedImages.has(vcard._id) ? (
                    <img 
                      src={vcard.profileImage.startsWith('http') || vcard.profileImage.startsWith('data:')
                        ? vcard.profileImage 
                        : (() => {
                            const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
                            return `${baseUrl}${vcard.profileImage}`;
                          })()} 
                      alt={vcard.fullName} 
                      className="vcard-card-image"
                      onError={(e) => {
                        setFailedImages(prev => new Set(prev).add(vcard._id));
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : null}
                  {(!vcard.profileImage || failedImages.has(vcard._id)) && (
                    <div 
                      className="vcard-card-placeholder"
                      style={{ background: getAvatarColor(vcard.fullName) }}
                    >
                      {getInitials(vcard.fullName)}
                    </div>
                  )}
                  <h3>{vcard.fullName}</h3>
                </div>
                <div className="vcard-card-qr">
                  <div id={`qr-container-${vcard._id}`} style={{ minHeight: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}></div>
                  <button
                    onClick={() => handleDownloadQR(vcard)}
                    className="btn-download-qr"
                    title="Download QR code (without frame)"
                  >
                    <FaDownload />
                  </button>
                </div>
                <div className="vcard-card-actions">
                  <Link to={`/vcard/${vcard.uniqueId}`} target="_blank" className="btn btn-view">
                    View
                  </Link>
                  <Link to={`/vcard/edit/${vcard._id}`} className="btn btn-edit">
                    Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(vcard._id)}
                    className="btn btn-danger"
                  >
                    Delete
                  </button>
                </div>
                <div className="vcard-card-link">
                  <div className="vcard-url-container">
                    <small className="vcard-url-text">{FRONTEND_URL}/vcard/{vcard.uniqueId}</small>
                    <button
                      onClick={() => handleCopyUrl(vcard.uniqueId)}
                      className="btn-copy-url"
                      title="Copy to clipboard"
                    >
                      {copiedId === vcard.uniqueId ? (
                        <FaCheck className="copy-icon" />
                      ) : (
                        <FaCopy className="copy-icon" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
